import type { WorldDocument } from "daedalus-core";
import { FIELD_CELL_PX, tilesPerCropCell } from "./field-cells.ts";
import { readMapFields, type CompiledField } from "./field-map.ts";

/**
 * A world's field files (ADR-144 decision 6): `server/fields.json`, which the map server and the farm NPC read, holding
 * each field's owner, crop cells and patches, and the public `field-cells.json`, which a page reads to know which cells
 * of the chunk it is in are farmland and of which kind. A world with no `field` marker writes neither.
 */

/**
 * The server part's path in a version directory.
 */
export const FIELDS_FILE = "server/fields.json";

/**
 * The public cell list's path in a version directory.
 */
export const FIELD_CELLS_FILE = "field-cells.json";

/**
 * The version of both files' layout.
 */
export const FIELD_FILES_SCHEMA = 1;

/**
 * Orders two strings by code unit, which no locale changes.
 *
 * @param a One string.
 * @param b Another.
 * @returns A negative number, zero or a positive one, as a sort wants.
 */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Every field of a world, whole, ordered by map then id so the order maps and markers are laid in changes nothing.
 *
 * @param world The world.
 * @returns The fields.
 * @throws Error naming the map and each error of its fields, one to a line, when any field has one; a warning, such as
 *   cells left over after carving, does not refuse.
 */
export function compileWorldFields(world: Pick<WorldDocument, "maps" | "tileSize">): CompiledField[] {
  const fields: CompiledField[] = [];
  const problems: string[] = [];
  for (const map of world.maps) {
    const read = readMapFields(map, world.tileSize);
    fields.push(...read.fields);
    problems.push(...read.issues.filter((issue) => issue.severity !== "warning").map((issue) => `map ${map.id}: ${issue.message}`));
  }
  if (problems.length > 0) {
    throw new Error(problems.join("\n"));
  }
  return fields.sort((a, b) => byCodeUnit(a.map, b.map) || byCodeUnit(a.id, b.id));
}

/**
 * The text of the server file.
 *
 * @param fields The world's fields.
 * @returns Indented JSON with a final newline.
 */
/**
 * A file's text with one field a line, so a diff of two versions reads field by field.
 *
 * @param fields The fields.
 * @param extra Keys written after the list.
 * @returns JSON with a final newline.
 */
function listText(fields: readonly unknown[], extra: Readonly<Record<string, unknown>> = {}): string {
  const rows = fields.map((field) => `    ${JSON.stringify(field)}`).join(",\n");
  const tail = Object.entries(extra).map(([key, value]) => `,\n  ${JSON.stringify(key)}: ${JSON.stringify(value)}`).join("");
  return `{\n  "schema": ${FIELD_FILES_SCHEMA},\n  "cellPx": ${FIELD_CELL_PX},\n  "fields": [${rows === "" ? "" : `\n${rows}\n  `}]${tail}\n}\n`;
}

/**
 * The text of the public file: each field's map, id and kind, and per map and chunk the runs `[field, column, row,
 * length]` of crop cells that lie in it, a run that crosses a chunk's edge split there.
 *
 * @param fields The world's fields.
 * @param tileSize The world's tile size in pixels.
 * @param chunkSize The world's chunk size in tiles.
 * @returns Indented JSON with a final newline.
 * @throws Error when a chunk is not a whole number of crop cells.
 */
function publicText(fields: readonly CompiledField[], tileSize: number, chunkSize: number): string {
  const per = tilesPerCropCell(tileSize) as number;
  if (chunkSize % per !== 0) {
    throw new Error(`a chunk of ${chunkSize} tiles is not a whole number of crop cells of ${per} tiles`);
  }
  const span = chunkSize / per;
  const maps: Record<string, Map<string, [number, number, number, number][]>> = {};
  fields.forEach((field, index) => {
    const chunks = (maps[field.map] ??= new Map());
    for (const [column, row, length] of field.runs) {
      const cy = Math.floor(row / span);
      for (let at = column; at < column + length; ) {
        const cx = Math.floor(at / span);
        const end = Math.min(column + length, (cx + 1) * span);
        const key = `${cx}_${cy}`;
        const runs = chunks.get(key) ?? [];
        runs.push([index, at, row, end - at]);
        chunks.set(key, runs);
        at = end;
      }
    }
  });
  const named = Object.fromEntries(
    Object.entries(maps)
      .sort(([a], [b]) => byCodeUnit(a, b))
      .map(([map, chunks]) => [map, Object.fromEntries([...chunks].sort(([a], [b]) => chunkOrder(a) - chunkOrder(b)).map(([key, runs]) => [key, runs.sort((a, b) => a[2] - b[2] || a[1] - b[1] || a[0] - b[0])]))]),
  );
  return listText(fields.map((field) => ({ map: field.map, id: field.id, kind: field.kind })), { maps: named });
}

/**
 * The place of a chunk key in reading order, rows first.
 *
 * @param key The key, `<column>_<row>`.
 * @returns A number that sorts chunks by row then column.
 */
function chunkOrder(key: string): number {
  const [cx, cy] = key.split("_").map(Number) as [number, number];
  return cy * 1_000_000 + cx;
}

/**
 * The files a world's fields need in its version, as text by their paths in the version directory.
 *
 * @param world The world.
 * @returns The files; none when the world has no field.
 * @throws Error as `compileWorldFields` throws, or when a chunk is not a whole number of crop cells.
 */
export function fieldFilesOf(world: Pick<WorldDocument, "maps" | "tileSize" | "chunkSize">): Readonly<Record<string, string>> {
  const fields = compileWorldFields(world);
  if (fields.length === 0) {
    return {};
  }
  return { [FIELDS_FILE]: listText(fields), [FIELD_CELLS_FILE]: publicText(fields, world.tileSize, world.chunkSize) };
}
